import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FEATURED_JOBS } from "../../../mocks/jobs";

type SortKey = "match" | "pay" | "distance" | "urgency";

const URGENCY_ORDER: Record<string, number> = { URGENT: 0, TODAY: 1, TOMORROW: 2, "THIS WEEK": 3, FLEXIBLE: 4 };
const URGENCY_COLORS: Record<string, string> = {
  URGENT: "bg-red-100 text-red-700",
  TODAY: "bg-red-50 text-red-600",
  TOMORROW: "bg-orange-100 text-orange-700",
  "THIS WEEK": "bg-yellow-100 text-yellow-700",
  FLEXIBLE: "bg-green-100 text-green-700",
};

function JobCard({ job, onApply, onView }: { job: (typeof FEATURED_JOBS)[0]; onApply: (id: number) => void; onView: (id: number) => void }) {
  return (
    <div onClick={() => onView(job.id)} className="bg-white rounded-2xl border border-gray-200 p-5 flex flex-col gap-4 hover:border-emerald-200 transition-all duration-200 group cursor-pointer">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gray-50 border border-gray-100 shrink-0">
            <i className={`${job.icon} text-xl text-gray-600`} />
          </div>
          <div>
            <h4 className="font-bold text-gray-900 text-sm leading-snug">{job.title}</h4>
            <p className="text-xs text-gray-500">{job.company}</p>
          </div>
        </div>
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${URGENCY_COLORS[job.urgency] || "bg-gray-100 text-gray-600"}`}>
          {job.urgency}
        </span>
      </div>

      {/* Pay + Distance */}
      <div className="flex items-center gap-4">
        <div>
          <div className="text-xl font-extrabold text-gray-900">{job.pay}</div>
          <div className="text-xs text-gray-400">pay rate</div>
        </div>
        <div className="w-px h-8 bg-gray-100" />
        <div>
          <div className="text-sm font-bold text-gray-700">{job.distance}</div>
          <div className="text-xs text-gray-400">distance</div>
        </div>
        <div className="w-px h-8 bg-gray-100" />
        <div>
          <div className="text-sm font-bold text-gray-700">{job.slots} slots</div>
          <div className="text-xs text-gray-400">open</div>
        </div>
      </div>

      {/* Description */}
      <p className="text-xs text-gray-500 leading-relaxed">{job.description}</p>

      {/* Capabilities */}
      <div className="flex flex-wrap gap-1.5">
        {job.capabilities.map((cap) => (
          <span key={cap} className="text-xs bg-gray-50 border border-gray-200 text-gray-600 px-2.5 py-1 rounded-lg">
            {cap}
          </span>
        ))}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 flex items-center justify-center bg-green-50 rounded-full">
            <i className="ri-bar-chart-fill text-green-600 text-xs" />
          </div>
          <span className="text-xs font-semibold text-green-700">{job.match}% match</span>
          <span className="text-xs text-gray-400 ml-2">{job.posted}</span>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onApply(job.id); }}
          className="px-4 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          Quick Apply
        </button>
      </div>
    </div>
  );
}

export default function MarketplaceSection() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"find" | "hire">("find");
  const [sortBy, setSortBy] = useState<SortKey>("urgency");
  const [appliedIds, setAppliedIds] = useState<number[]>([]);
  const [hireSubmitted, setHireSubmitted] = useState(false);
  const [hireForm, setHireForm] = useState({ name: "", email: "", jobType: "", description: "", pay: "", urgency: "TODAY" });

  const sortedJobs = [...FEATURED_JOBS].sort((a, b) => {
    if (sortBy === "pay") return b.payNum - a.payNum;
    if (sortBy === "distance") return a.distanceNum - b.distanceNum;
    if (sortBy === "match") return b.match - a.match;
    return (URGENCY_ORDER[a.urgency] ?? 5) - (URGENCY_ORDER[b.urgency] ?? 5);
  });

  const handleApply = (id: number) => {
    setAppliedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const handleHireSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((v, k) => data.append(k, v.toString()));
    try {
      await fetch("https://readdy.ai/api/form/d75cvlivu4krcbn3bki0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
    } finally {
      setHireSubmitted(true);
    }
  };

  return (
    <section id="marketplace" className="hidden md:block py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-blue-600 bg-blue-50 px-4 py-1.5 rounded-full mb-4">
            Dual Marketplace
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-4">
            Work. Hire. Repeat.
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            Whether you're looking for work or need workers fast — the marketplace handles it all.
          </p>
        </div>

        {/* Tab Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-gray-100 rounded-full p-1 flex gap-1">
            <button
              onClick={() => setTab("find")}
              className={`px-6 py-2.5 text-sm font-bold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                tab === "find" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className="ri-search-line mr-2" />
              Find Work
            </button>
            <button
              onClick={() => setTab("hire")}
              className={`px-6 py-2.5 text-sm font-bold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap ${
                tab === "hire" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <i className="ri-briefcase-line mr-2" />
              Hire Now
            </button>
          </div>
        </div>

        {/* Find Work Tab */}
        {tab === "find" && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              <span className="text-sm font-semibold text-gray-600">Sort by:</span>
              {(["urgency", "pay", "match", "distance"] as SortKey[]).map((key) => (
                <button
                  key={key}
                  onClick={() => setSortBy(key)}
                  className={`px-4 py-2 text-xs font-bold rounded-lg border transition-colors cursor-pointer whitespace-nowrap capitalize ${
                    sortBy === key
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-600 border-gray-200 hover:border-blue-300"
                  }`}
                >
                  {key === "urgency" ? "Most Urgent" : key === "pay" ? "Highest Pay" : key === "match" ? "Best Match" : "Nearest"}
                </button>
              ))}
              <div className="ml-auto text-sm text-gray-500 font-medium">
                {sortedJobs.length} opportunities found
              </div>
            </div>

            {/* Applied notification */}
            {appliedIds.length > 0 && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-xl px-5 py-3 flex items-center gap-3">
                <i className="ri-check-double-line text-green-600 text-lg" />
                <span className="text-sm font-semibold text-green-800">
                  Applied to {appliedIds.length} job{appliedIds.length > 1 ? "s" : ""}! AI is generating your tailored resume and cover letter.
                </span>
              </div>
            )}

            {/* Job Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {sortedJobs.map((job) => (
                <div key={job.id} className={`${appliedIds.includes(job.id) ? "opacity-60" : ""}`}>
                  <JobCard job={job} onApply={handleApply} onView={(id) => navigate(`/jobs/${id}`)} />
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <button
                onClick={() => navigate("/find-work")}
                className="px-8 py-3 border border-gray-300 text-gray-600 font-semibold rounded-xl text-sm hover:border-emerald-400 hover:text-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Load More Jobs
                <i className="ri-arrow-right-line ml-2" />
              </button>
            </div>
          </>
        )}

        {/* Hire Now Tab */}
        {tab === "hire" && (
          <div className="max-w-2xl mx-auto">
            {hireSubmitted ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-double-line text-green-600 text-3xl" />
                </div>
                <h3 className="text-2xl font-extrabold text-gray-900 mb-2">Job Posted!</h3>
                <p className="text-gray-500 mb-6">
                  We're matching your post with available workers right now. You'll hear back within minutes.
                </p>
                <button
                  onClick={() => { setHireSubmitted(false); setHireForm({ name: "", email: "", jobType: "", description: "", pay: "", urgency: "TODAY" }); }}
                  className="px-6 py-2.5 bg-blue-600 text-white font-bold rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-blue-700 transition-colors"
                >
                  Post Another Job
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleHireSubmit}
                className="bg-white rounded-2xl border border-gray-200 p-8 flex flex-col gap-5"
              >
                <div>
                  <h3 className="text-xl font-extrabold text-gray-900 mb-1">Post an Urgent Job</h3>
                  <p className="text-sm text-gray-500">Get matched with qualified workers within minutes.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Your Name</label>
                    <input
                      name="name"
                      type="text"
                      required
                      value={hireForm.name}
                      onChange={(e) => setHireForm({ ...hireForm, name: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address</label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={hireForm.name}
                      onChange={(e) => setHireForm({ ...hireForm, email: e.target.value })}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Job Category</label>
                    <select
                      name="jobType"
                      required
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                    >
                      <option value="">Select category</option>
                      <option>Landscaping</option>
                      <option>Moving / Labor</option>
                      <option>Cleaning</option>
                      <option>Construction</option>
                      <option>Events</option>
                      <option>Personal Services</option>
                      <option>Driving / Delivery</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1.5">Urgency</label>
                    <select
                      name="urgency"
                      required
                      className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white cursor-pointer"
                    >
                      <option>TODAY</option>
                      <option>TOMORROW</option>
                      <option>THIS WEEK</option>
                      <option>FLEXIBLE</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Pay Rate</label>
                  <input
                    name="pay"
                    type="text"
                    required
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. $25/hr or $200 flat"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-1.5">Job Description</label>
                  <textarea
                    name="description"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Describe the job, location, and what workers need to bring..."
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl text-sm hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-line mr-2" />
                  Post Job & Find Workers
                </button>
                <p className="text-xs text-center text-gray-400">10–20% platform commission on completed jobs</p>
              </form>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
